/**
 * Iter-211 — Paid invoices ledger.
 *
 * CEO + GESTIONNAIRE-only page that lists every invoice marked paid
 * with the captured payment details (date, mode, reference, bank account,
 * amount, comment). Supports date-range filtering and PDF / CSV / Excel
 * exports for accounting handover.
 *
 * Routes consumed:
 *  - GET /api/invoices/paid?date_from=...&date_to=...
 *  - GET /api/invoices/paid/export.{pdf|csv|xlsx}?...
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, Filter, Loader2, Receipt, RefreshCw, Upload, Banknote, AlertCircle, Check, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const MODE_BADGE_COLOR = {
  Virement: "bg-blue-50 text-blue-700 border-blue-200",
  Chèque: "bg-purple-50 text-purple-700 border-purple-200",
  Espèces: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Carte: "bg-amber-50 text-amber-700 border-amber-200",
  Prélèvement: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Autre: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function PaidInvoices() {
  // Default range: current calendar year.
  const today = new Date();
  const defaultFrom = `${today.getFullYear()}-01-01`;
  const defaultTo = today.toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Iter-212 — Bank reconciliation state.
  const fileRef = useRef(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [tolerance, setTolerance] = useState(5);
  const [parsedTxns, setParsedTxns] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchSummary, setMatchSummary] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices/paid", {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot load paid invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalsByCurrency = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const c = r.currency || "MAD";
      m[c] = (m[c] || 0) + (r.paid_amount || 0);
    }
    return Object.entries(m).map(([currency, total]) => ({ currency, total }));
  }, [rows]);

  const download = async (fmt) => {
    try {
      const r = await api.get(`/invoices/paid/export.${fmt}`, {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `paid_invoices_${dateFrom || "all"}_${dateTo || "all"}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Cannot export ${fmt.toUpperCase()}`);
    }
  };

  // Iter-212 — Bank reconciliation handlers.
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true);
    setParsedTxns([]);
    setMatches([]);
    setMatchSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/invoices/paid/reconcile/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const txns = r.data.transactions || [];
      if (txns.length === 0) {
        toast.error("Aucune transaction créditrice détectée dans le fichier.");
        setParsing(false);
        return;
      }
      setParsedTxns(txns);
      // Auto-match right after parsing.
      const m = await api.post("/invoices/paid/reconcile/match", {
        transactions: txns,
        tolerance_pct: tolerance,
      });
      setMatches(m.data.matches);
      setMatchSummary(m.data.summary);
      toast.success(`${txns.length} transactions analysées · ${m.data.summary.matched_exact + m.data.summary.matched_reference + m.data.summary.matched_amount + m.data.summary.matched_fuzzy} matchées`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du parsing");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rerunMatch = async () => {
    if (parsedTxns.length === 0) return;
    setParsing(true);
    try {
      const m = await api.post("/invoices/paid/reconcile/match", {
        transactions: parsedTxns,
        tolerance_pct: tolerance,
      });
      setMatches(m.data.matches);
      setMatchSummary(m.data.summary);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setParsing(false); }
  };

  const applyMatches = async () => {
    const toApply = matches.filter((m) => m.invoice_id);
    if (toApply.length === 0) { toast.error("Aucun match à appliquer."); return; }
    setApplying(true);
    try {
      const r = await api.post("/invoices/paid/reconcile/apply", { matches: toApply });
      toast.success(`${r.data.count_applied} facture(s) marquée(s) payée(s)`);
      setReconcileOpen(false);
      setParsedTxns([]);
      setMatches([]);
      setMatchSummary(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'application");
    } finally { setApplying(false); }
  };

  const clearMatch = (txnId) => {
    setMatches((cur) => cur.map((m) => m.txn_id === txnId ? { ...m, invoice_id: null, bank_commission: null, confidence: "none", notes: "Désélectionné" } : m));
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="paid-invoices-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1 font-mono">Finance · Cabinet</div>
          <h1 className="text-2xl font-semibold text-brand-navy flex items-center gap-2">
            <Receipt size={22} /> Factures payées
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Registre des paiements clients avec mode, référence, compte bénéficiaire et commentaire. Filtre par période et exports PDF / CSV / Excel pour la comptabilité.
          </p>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 flex flex-col gap-3 md:flex-row md:items-end md:gap-4 md:flex-wrap">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 font-mono">
          <Filter size={14} /> Période
        </div>
        <div className="grid grid-cols-2 gap-3 md:flex md:items-end md:gap-3 flex-1">
          <div>
            <Label htmlFor="pi-from">Du</Label>
            <Input id="pi-from" data-testid="paid-invoices-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="pi-to">Au</Label>
            <Input id="pi-to" data-testid="paid-invoices-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={load}
            disabled={loading}
            data-testid="paid-invoices-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600 disabled:opacity-50"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Rafraîchir
          </button>
          <button
            onClick={() => download("pdf")}
            disabled={loading || rows.length === 0}
            data-testid="paid-invoices-export-pdf"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-red-50 text-red-700 hover:bg-red-100 text-xs font-semibold rounded-md border border-red-200 disabled:opacity-50"
          >
            <FileText size={13} /> PDF
          </button>
          <button
            onClick={() => download("csv")}
            disabled={loading || rows.length === 0}
            data-testid="paid-invoices-export-csv"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-zinc-50 text-zinc-800 hover:bg-zinc-100 text-xs font-semibold rounded-md border border-zinc-300 disabled:opacity-50"
          >
            <Download size={13} /> CSV
          </button>
          <button
            onClick={() => download("xlsx")}
            disabled={loading || rows.length === 0}
            data-testid="paid-invoices-export-xlsx"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold rounded-md border border-emerald-200 disabled:opacity-50"
          >
            <FileSpreadsheet size={13} /> Excel
          </button>
          <button
            onClick={() => setReconcileOpen(true)}
            data-testid="open-reconcile"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600"
          >
            <Banknote size={13} /> Rapprochement bancaire
          </button>
        </div>
      </div>

      {/* Iter-212 — Bank reconciliation panel (inline, expanding) */}
      {reconcileOpen && (
        <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4" data-testid="reconcile-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-brand-navy flex items-center gap-2">
                <Banknote size={18} /> Rapprochement bancaire
              </h2>
              <p className="text-xs text-zinc-600 mt-1">
                Importez un relevé bancaire (CSV ou OFX). Le système matche automatiquement chaque transaction à une facture émise non payée — par référence puis par montant. La commission bancaire (différence négative pour les virements EUR convertis) est détectée et archivée sur la facture.
              </p>
            </div>
            <button onClick={() => setReconcileOpen(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="close-reconcile"><X size={18} /></button>
          </div>

          {/* Upload row */}
          <div className="flex flex-wrap items-end gap-3 bg-brand-cream-50 border border-brand-cream-200 rounded-md p-4">
            <div className="flex-1 min-w-[200px]">
              <Label>Fichier bancaire (CSV / OFX)</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.ofx,.qfx,text/csv,application/x-ofx"
                onChange={handleFile}
                disabled={parsing}
                data-testid="reconcile-file"
                className="block w-full text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-brand-navy file:text-white file:text-xs file:font-semibold hover:file:bg-brand-navy-600"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Formats supportés : CSV (séparateur ; ou ,) avec colonnes Date + Montant + Référence, ou OFX.</p>
            </div>
            <div>
              <Label>Tolérance FX (%)</Label>
              <Input
                type="number" min="0" max="20" step="0.5"
                value={tolerance}
                onChange={(e) => setTolerance(parseFloat(e.target.value) || 0)}
                data-testid="reconcile-tolerance"
                className="w-24"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Commission bancaire max acceptée.</p>
            </div>
            {parsedTxns.length > 0 && (
              <button onClick={rerunMatch} disabled={parsing} className="inline-flex items-center gap-1.5 px-3 h-10 bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold rounded-md disabled:opacity-50" data-testid="reconcile-rematch">
                <RefreshCw size={13} /> Re-matcher
              </button>
            )}
          </div>

          {parsing && (
            <div className="text-center py-8 text-sm text-zinc-500">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" /> Analyse en cours…
            </div>
          )}

          {/* Summary */}
          {matchSummary && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 px-3 h-8 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md font-mono">
                <Check size={13} /> Exact : {matchSummary.matched_exact}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 h-8 bg-blue-50 text-blue-800 border border-blue-200 rounded-md font-mono">
                Par référence : {matchSummary.matched_reference}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 h-8 bg-purple-50 text-purple-800 border border-purple-200 rounded-md font-mono">
                Montant : {matchSummary.matched_amount}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 h-8 bg-amber-50 text-amber-800 border border-amber-200 rounded-md font-mono">
                Fuzzy FX : {matchSummary.matched_fuzzy}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 h-8 bg-red-50 text-red-800 border border-red-200 rounded-md font-mono">
                <AlertCircle size={13} /> Non matchées : {matchSummary.unmatched}
              </span>
              <span className="inline-flex items-center px-3 h-8 bg-zinc-50 border border-zinc-200 rounded-md font-mono text-zinc-700">
                {matchSummary.total} transaction{matchSummary.total > 1 ? "s" : ""} · {matchSummary.outstanding_invoices} facture(s) en attente
              </span>
            </div>
          )}

          {/* Matches table */}
          {matches.length > 0 && (
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-brand-navy text-white uppercase tracking-wider font-mono text-[10px]">
                      <th className="px-3 py-2.5 text-left">Date</th>
                      <th className="px-3 py-2.5 text-right">Montant reçu</th>
                      <th className="px-3 py-2.5 text-left">Référence bancaire</th>
                      <th className="px-3 py-2.5 text-left">Facture matchée</th>
                      <th className="px-3 py-2.5 text-right">Total facturé</th>
                      <th className="px-3 py-2.5 text-right">Commission</th>
                      <th className="px-3 py-2.5 text-center">Niveau</th>
                      <th className="px-3 py-2.5 text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m) => {
                      const txn = parsedTxns.find((t) => t.txn_id === m.txn_id);
                      const conf = m.confidence;
                      const confColor = {
                        exact: "bg-emerald-100 text-emerald-800",
                        reference: "bg-blue-100 text-blue-800",
                        amount: "bg-purple-100 text-purple-800",
                        fuzzy: "bg-amber-100 text-amber-800",
                        none: "bg-zinc-100 text-zinc-600",
                      }[conf] || "bg-zinc-100";
                      return (
                        <tr key={m.txn_id} className="border-t border-zinc-100 hover:bg-brand-cream-50" data-testid={`reconcile-row-${m.txn_id}`}>
                          <td className="px-3 py-2 font-mono">{txn?.date || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {(txn?.amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {txn?.currency || ""}
                          </td>
                          <td className="px-3 py-2 font-mono truncate max-w-[180px]" title={txn?.reference || txn?.description || ""}>
                            {txn?.reference || txn?.description || "—"}
                          </td>
                          <td className="px-3 py-2">
                            {m.invoice ? (
                              <div>
                                <div className="font-mono font-semibold">{m.invoice.invoice_number || m.invoice.number}</div>
                                <div className="text-[10px] text-zinc-500 truncate max-w-[180px]" title={m.invoice.client_name}>{m.invoice.client_name}</div>
                              </div>
                            ) : <span className="text-zinc-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {m.invoice ? `${(m.invoice.total_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${m.invoice.currency}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {m.bank_commission ? (
                              <span className="text-orange-700">{m.bank_commission.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</span>
                            ) : <span className="text-zinc-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${confColor}`}>{conf}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {m.invoice_id && (
                              <button onClick={() => clearMatch(m.txn_id)} className="text-zinc-400 hover:text-red-700" title="Désélectionner" data-testid={`unmatch-${m.txn_id}`}>
                                <X size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {matches.length > 0 && (
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setReconcileOpen(false)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Annuler</button>
              <button
                onClick={applyMatches}
                disabled={applying || matches.filter(m => m.invoice_id).length === 0}
                data-testid="reconcile-apply"
                className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50"
              >
                {applying ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Appliquer {matches.filter(m => m.invoice_id).length} match(es)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Totals */}
      {totalsByCurrency.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="paid-invoices-totals">
          <span className="text-xs uppercase tracking-wider text-zinc-500 font-mono self-center">Totaux</span>
          {totalsByCurrency.map(({ currency, total }) => (
            <span
              key={currency}
              className="inline-flex items-center gap-2 px-3 h-9 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md text-xs font-mono"
              data-testid={`paid-invoices-total-${currency}`}
            >
              <strong>{total.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              <span>{currency}</span>
            </span>
          ))}
          <span className="inline-flex items-center px-3 h-9 bg-zinc-50 border border-zinc-200 rounded-md text-xs font-mono text-zinc-700">
            {rows.length} facture{rows.length > 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-navy text-white text-[11px] uppercase tracking-wider font-mono">
                <th className="px-3 py-3 text-left">Date paiement</th>
                <th className="px-3 py-3 text-left">N° facture</th>
                <th className="px-3 py-3 text-left">Client</th>
                <th className="px-3 py-3 text-left">Mission</th>
                <th className="px-3 py-3 text-left">Mois facturé</th>
                <th className="px-3 py-3 text-right">Montant facture</th>
                <th className="px-3 py-3 text-right">Montant payé</th>
                <th className="px-3 py-3 text-right">Delta</th>
                <th className="px-3 py-3 text-left">Mode</th>
                <th className="px-3 py-3 text-left">Référence</th>
                <th className="px-3 py-3 text-left">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-3 py-12 text-center text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-12 text-center text-zinc-500" data-testid="paid-invoices-empty">
                  Aucune facture payée sur cette période.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.invoice_id} data-testid={`paid-row-${r.invoice_id}`} className="border-t border-zinc-100 hover:bg-brand-cream-50">
                  <td className="px-3 py-2.5 font-mono text-xs">{r.paid_at || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.invoice_number || "—"}</td>
                  <td className="px-3 py-2.5">{r.client_name || "—"}</td>
                  <td className="px-3 py-2.5 truncate max-w-[200px]" title={r.mission_title || ""}>{r.mission_title || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.year && r.month ? `${r.year}-${String(r.month).padStart(2, "0")}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" data-testid={`paid-invoice-amount-${r.invoice_id}`}>
                    {r.total_ttc != null ? `${r.total_ttc.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${r.currency}` : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" data-testid={`paid-amount-${r.invoice_id}`}>
                    {r.paid_amount_mad ? (
                      <span>
                        {r.paid_amount_mad.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                        <span className="block text-[10px] text-zinc-400">
                          ({r.paid_amount?.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.currency})
                        </span>
                      </span>
                    ) : (
                      <>{r.paid_amount?.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "—"} {r.currency}</>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" data-testid={`paid-delta-${r.invoice_id}`}>
                    {r.delta == null ? <span className="text-zinc-400">—</span> : Math.abs(r.delta) < 0.005 ? (
                      <span className="text-zinc-400">0,00</span>
                    ) : (
                      <span
                        className={r.delta > 0 ? "text-red-700 font-semibold" : "text-sky-700 font-semibold"}
                        title={r.delta > 0 ? "Montant reçu inférieur à la facture (retenue, commission…)" : "Montant reçu supérieur à la facture"}
                      >
                        {r.delta > 0 ? "−" : "+"}{Math.abs(r.delta).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.currency}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.payment_mode_label ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${MODE_BADGE_COLOR[r.payment_mode_label] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                        {r.payment_mode_label}
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.payment_reference || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2.5 text-xs text-zinc-600 truncate max-w-[200px]" title={r.payment_comment || ""}>{r.payment_comment || <span className="text-zinc-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
